import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Building2, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  Filter, 
  Mail, 
  Phone, 
  Search, 
  UserPlus, 
  Users,
  BadgeCheck,
  Clock,
  Loader2,
  ArrowUp,
  ArrowDown,
  MoreHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Dropdown from "@/components/ui/dropdown";
import { Employee, EmployeeFilterOptions } from "@/types/employee";
import AddEmployeeModal from "@/components/employee/AddEmployeeModal";
import { useToast } from "@/hooks/use-toast";
import { createEmployee, deleteEmployee, fetchEmployees, updateEmployee } from "@/utils/supabase/employeeUtils";
import { fetchDepartments } from "@/utils/supabase/departmentUtils";
import { Skeleton } from "@/components/ui/skeleton";
import { Department } from "@/types/department";
import EditEmployeeModal from "@/components/employee/EditEmployeeModal";

const Employees = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [filters, setFilters] = useState<EmployeeFilterOptions>({
    department: "all",
    status: "all",
    search: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const recordsPerPage = 10;
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Employee | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        const [employeesData, departmentsData] = await Promise.all([
          fetchEmployees(filters),
          fetchDepartments()
        ]);
        
        setEmployees(employeesData);
        setDepartments(departmentsData);
      } catch (error) {
        console.error("Failed to load data:", error);
        toast({
          title: "Error",
          description: "Failed to load employee data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [filters, toast]);

  // Filter and sort employees
  useEffect(() => {
    let result = [...employees];
    
    // Apply global search if there is any
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      result = employees.filter(emp => 
        emp.name.toLowerCase().includes(searchLower) ||
        emp.email.toLowerCase().includes(searchLower) ||
        emp.position.toLowerCase().includes(searchLower) ||
        emp.phone?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting if set
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredEmployees(result);
    setTotalPages(Math.ceil(result.length / recordsPerPage));
    // Reset to first page when filters or sorting changes
    setCurrentPage(1);
  }, [employees, filters.search, sortConfig]);

  const departmentOptions = [
    { label: "All Departments", value: "all" },
    ...departments.map(dept => ({ label: dept.name, value: dept.id }))
  ];
  
  const statusOptions = [
    { label: "All Statuses", value: "all" },
    { 
      label: "Active", 
      value: "active",
      icon: <BadgeCheck className="h-4 w-4 text-green-500" />
    },
    { 
      label: "On Leave", 
      value: "on-leave",
      icon: <Clock className="h-4 w-4 text-amber-500" />
    },
    { 
      label: "Inactive", 
      value: "inactive",
      icon: <Clock className="h-4 w-4 text-gray-500" />
    },
  ];
  
  const getDepartmentName = (departmentId: string | undefined) => {
    if (!departmentId) return "Unassigned";
    const department = departments.find(dept => dept.id === departmentId);
    return department ? department.name : "Unknown Department";
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case "inactive":
        return <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>;
      case "on-leave":
        return <Badge className="bg-amber-100 text-amber-800">On Leave</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };
  
  const handleAddEmployee = async (newEmployee: {
    name: string;
    email: string;
    position: string;
    phone: string;
    status: string;
    start_date: string;
    department_id?: string;
  }) => {
    try {
      const employeeToCreate = {
        name: newEmployee.name,
        email: newEmployee.email,
        position: newEmployee.position,
        departmentId: newEmployee.department_id,
        phone: newEmployee.phone,
        startDate: newEmployee.start_date,
        status: newEmployee.status,
        skills: []
      };
      
      const createdEmployee = await createEmployee(employeeToCreate);
      setEmployees(prev => [...prev, createdEmployee]);
      
      toast({
        title: "Employee Added",
        description: `${newEmployee.name} has been added successfully.`,
      });
      setShowAddModal(false);
    } catch (error) {
      console.error("Error adding employee:", error);
      toast({
        title: "Error",
        description: "Failed to add employee",
        variant: "destructive",
      });
    }
  };
  
  const handleDeleteEmployee = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        await deleteEmployee(id);
        setEmployees(prev => prev.filter(emp => emp.id !== id));
        
        toast({
          title: "Employee Deleted",
          description: `${name} has been removed successfully.`,
        });
      } catch (error) {
        console.error("Error deleting employee:", error);
        toast({
          title: "Error",
          description: "Failed to delete employee",
          variant: "destructive",
        });
      }
    }
  };
  
  const handleEditEmployee = async (updatedEmployee: {
    id: string;
    name: string;
    email: string;
    position: string;
    phone: string;
    status: string;
    start_date: string;
    department_id?: string;
  }) => {
    try {
      await updateEmployee(updatedEmployee.id, {
        name: updatedEmployee.name,
        email: updatedEmployee.email,
        position: updatedEmployee.position,
        phone: updatedEmployee.phone,
        status: updatedEmployee.status,
        departmentId: updatedEmployee.department_id,
        startDate: updatedEmployee.start_date,
      });
      setEmployees(prev => prev.map(emp => emp.id === updatedEmployee.id ? { ...emp, ...updatedEmployee, departmentId: updatedEmployee.department_id } : emp));
      toast({
        title: "Employee Updated",
        description: `${updatedEmployee.name}'s details have been updated successfully.`,
      });
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating employee:", error);
      toast({
        title: "Error",
        description: "Failed to update employee",
        variant: "destructive",
      });
    }
  };
  
  const exportEmployees = () => {
    const headers = ["Name", "Email", "Position", "Department", "Phone", "Start Date", "Status"];
    const csvRows = [
      headers.join(","),
      ...employees.map(emp => [
        emp.name,
        emp.email,
        emp.position,
        getDepartmentName(emp.departmentId),
        emp.phone || "N/A",
        emp.startDate,
        emp.status
      ].join(","))
    ];
    
    const csvContent = csvRows.join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "employees.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get current page items
  const getCurrentPageItems = () => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return filteredEmployees.slice(indexOfFirstRecord, indexOfLastRecord);
  };

  // Function to handle pagination
  const paginate = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Function to handle column sorting
  const handleSort = (key: keyof Employee) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Function to render sort icon
  const renderSortIcon = (key: keyof Employee) => {
    if (sortConfig.key !== key) {
      return (
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleSort(key)}>
          <ArrowUp className="h-4 w-4 text-muted-foreground" />
        </Button>
      );
    }
    return (
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleSort(key)}>
        {sortConfig.direction === 'asc' ? (
          <ArrowUp className="h-4 w-4 text-primary" />
        ) : (
          <ArrowDown className="h-4 w-4 text-primary" />
        )}
      </Button>
    );
  };

  // Function to truncate text with ellipsis
  const truncateText = (text: string, maxLength: number = 15) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  // Generate pagination buttons
  const paginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;
    
    // Always show first page
    buttons.push(
      <Button 
        key="page-1"
        variant={currentPage === 1 ? "default" : "outline"} 
        size="sm" 
        className={currentPage === 1 ? "bg-primary text-primary-foreground" : ""}
        onClick={() => paginate(1)}
      >
        1
      </Button>
    );
    
    let startPage = Math.max(2, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxButtons - 3);
    
    if (startPage > 2) {
      buttons.push(
        <span key="ellipsis-1" className="px-2">
          <MoreHorizontal className="h-4 w-4" />
        </span>
      );
    }
    
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button 
          key={`page-${i}`}
          variant={currentPage === i ? "default" : "outline"} 
          size="sm" 
          className={currentPage === i ? "bg-primary text-primary-foreground" : ""}
          onClick={() => paginate(i)}
        >
          {i}
        </Button>
      );
    }
    
    if (endPage < totalPages - 1) {
      buttons.push(
        <span key="ellipsis-2" className="px-2">
          <MoreHorizontal className="h-4 w-4" />
        </span>
      );
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      buttons.push(
        <Button 
          key={`page-${totalPages}`}
          variant={currentPage === totalPages ? "default" : "outline"} 
          size="sm" 
          className={currentPage === totalPages ? "bg-primary text-primary-foreground" : ""}
          onClick={() => paginate(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }
    
    return buttons;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold tracking-tight flex items-center">
          <Users className="h-6 w-6 mr-2" />
          Employees
        </h2>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={exportEmployees}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add Employee
          </Button>
        </div>
      </div>
      
      <div className="bg-card rounded-lg border shadow-sm animate-fade-in">
        <div className="p-4 border-b flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              className="pl-9"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Dropdown 
              label="Department"
              options={departmentOptions}
              value={filters.department}
              onChange={(value) => setFilters(prev => ({ ...prev, department: value }))}
              className="min-w-[180px]"
            />
            <Dropdown
              label="Status"
              options={statusOptions}
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              className="min-w-[140px]"
            />
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-medium mb-2">No employees found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {Object.values(filters).some(v => v !== 'all' && v !== '')
                  ? "No employees match your search criteria."
                  : "There are no employees in the system yet."}
              </p>
              <Button onClick={() => setShowAddModal(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Add Employee
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b transition-colors hover:bg-muted/50">
                  <th className="text-left p-4 text-sm font-medium">
                    <div className="flex items-center">
                      Employee
                      {renderSortIcon('name')}
                    </div>
                  </th>
                  <th className="text-left p-4 text-sm font-medium">
                    <div className="flex items-center">
                      Position
                      {renderSortIcon('position')}
                    </div>
                  </th>
                  <th className="text-left p-4 text-sm font-medium">
                    <div className="flex items-center">
                      Department
                      {renderSortIcon('departmentId')}
                    </div>
                  </th>
                  <th className="text-left p-4 text-sm font-medium">
                    <div className="flex items-center">
                      Contact
                      {renderSortIcon('email')}
                    </div>
                  </th>
                  <th className="text-left p-4 text-sm font-medium">
                    <div className="flex items-center">
                      Status
                      {renderSortIcon('status')}
                    </div>
                  </th>
                  <th className="text-left p-4 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentPageItems().map((employee) => (
                  <tr
                    key={employee.id}
                    className="border-b transition-colors hover:bg-muted/50"
                  >
                    <td className="p-4 align-middle">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={employee.avatar} alt={employee.name} />
                          <AvatarFallback>
                            {employee.name.split(" ").map(n => n[0]).join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium" title={employee.name}>
                            {truncateText(employee.name)}
                          </div>
                          <div className="text-sm text-muted-foreground" title={employee.email}>
                            {truncateText(employee.email)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 align-middle text-sm" title={employee.position}>
                      {truncateText(employee.position)}
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span title={getDepartmentName(employee.departmentId)}>
                          {truncateText(getDepartmentName(employee.departmentId))}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 align-middle">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm" title={employee.email}>
                          <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
                          {truncateText(employee.email)}
                        </div>
                        <div className="flex items-center text-sm" title={employee.phone || "N/A"}>
                          <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                          {truncateText(employee.phone || "N/A")}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 align-middle">
                      {getStatusBadge(employee.status)}
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/dashboard/employees/${employee.id}`)}
                        >
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedEmployee(employee); setShowEditModal(true); }}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteEmployee(employee.id, employee.name)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="p-4 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            Showing {filteredEmployees.length > 0 ? 
              `${(currentPage - 1) * recordsPerPage + 1}-${Math.min(currentPage * recordsPerPage, filteredEmployees.length)} of ${filteredEmployees.length}` : 
              '0'} employees
          </div>
          
          {filteredEmployees.length > 0 && (
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="hidden sm:flex items-center space-x-1">
                {paginationButtons()}
              </div>
              
              <div className="sm:hidden">
                <span className="text-sm font-medium">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
              
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
      
      <AddEmployeeModal 
        isOpen={showAddModal} 
        onOpenChange={setShowAddModal} 
        onSave={handleAddEmployee}
        departments={departments}
      />
      <EditEmployeeModal
        isOpen={showEditModal}
        onOpenChange={setShowEditModal}
        onSave={handleEditEmployee}
        employee={selectedEmployee}
        departments={departments}
      />
    </div>
  );
};

export default Employees;